import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Sparkles, 
  Mail, 
  FileText, 
  TrendingUp, 
  AlertTriangle,
  CheckCircle,
  Copy,
  Loader2,
  Brain,
  Zap,
  Target,
  MessageSquare,
  RefreshCw,
  ChevronRight,
  Users,
  DollarSign,
  Clock,
  ArrowUp,
  ArrowDown,
  FileSignature,
  Download,
  Percent,
  Package,
  Calendar
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AIFeatures() {
  const [activeTab, setActiveTab] = useState('insights');
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [insights, setInsights] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [emailType, setEmailType] = useState('follow_up');
  const [customContext, setCustomContext] = useState('');
  const [generatedEmail, setGeneratedEmail] = useState(null);
  const [meetingNotes, setMeetingNotes] = useState('');
  const [meetingSummary, setMeetingSummary] = useState(null);
  const [createTasks, setCreateTasks] = useState(true);
  const [copied, setCopied] = useState(false);
  
  // Proposal Generator State
  const [packageTier, setPackageTier] = useState('professional');
  const [includeDiscount, setIncludeDiscount] = useState(false);
  const [discountPercent, setDiscountPercent] = useState(10);
  const [proposalNotes, setProposalNotes] = useState('');
  const [generatedProposal, setGeneratedProposal] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchLeads();
    fetchInsights();
  }, []);

  const fetchLeads = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/leads`, { headers });
      setLeads(res.data);
      if (res.data.length > 0) setSelectedLead(res.data[0].id);
    } catch (err) {
      console.error('Failed to fetch leads:', err);
    }
  };

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/ai/deal-insights`, { headers });
      setInsights(res.data);
    } catch (err) {
      console.error('Failed to fetch insights:', err);
    }
    setLoading(false);
  };

  const generateEmail = async () => {
    if (!selectedLead) return;
    setLoading(true);
    setGeneratedEmail(null);
    try {
      const res = await axios.post(`${API_URL}/api/ai/generate-email`, {
        lead_id: selectedLead,
        email_type: emailType,
        custom_context: customContext || null
      }, { headers });
      setGeneratedEmail(res.data);
    } catch (err) {
      console.error('Failed to generate email:', err);
      alert('Failed to generate email. Please try again.');
    }
    setLoading(false);
  };

  const summarizeMeeting = async () => {
    if (!meetingNotes.trim()) return;
    setLoading(true);
    setMeetingSummary(null);
    try {
      const res = await axios.post(`${API_URL}/api/ai/summarize-meeting`, {
        meeting_notes: meetingNotes,
        lead_id: selectedLead || null,
        create_tasks: createTasks
      }, { headers });
      setMeetingSummary(res.data);
    } catch (err) {
      console.error('Failed to summarize meeting:', err);
      alert('Failed to summarize meeting. Please try again.');
    }
    setLoading(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getHealthColor = (health) => {
    if (health === 'Healthy') return 'text-green-400 bg-green-400/10';
    if (health === 'Needs Attention') return 'text-yellow-400 bg-yellow-400/10';
    return 'text-red-400 bg-red-400/10';
  };

  const getSentimentColor = (sentiment) => {
    if (sentiment?.toLowerCase().includes('positive')) return 'text-green-400';
    if (sentiment?.toLowerCase().includes('negative')) return 'text-red-400';
    return 'text-yellow-400';
  };

  const generateProposal = async () => {
    if (!selectedLead) return;
    setLoading(true);
    setGeneratedProposal(null);
    try {
      const res = await axios.post(`${API_URL}/api/ai/generate-proposal`, {
        lead_id: selectedLead,
        package_tier: packageTier,
        include_discount: includeDiscount,
        discount_percent: discountPercent,
        custom_notes: proposalNotes || null,
        validity_days: 30
      }, { headers });
      setGeneratedProposal(res.data);
    } catch (err) {
      console.error('Failed to generate proposal:', err);
      alert('Failed to generate proposal. Please try again.');
    }
    setLoading(false);
  };

  const downloadProposal = () => {
    if (!generatedProposal) return;
    const content = `
SALES PROPOSAL
==============
Proposal ID: ${generatedProposal.proposal_id}
Date: ${generatedProposal.created_date}
Valid Until: ${generatedProposal.valid_until}

PREPARED FOR:
${generatedProposal.client_name}
${generatedProposal.company}

EXECUTIVE SUMMARY
-----------------
${generatedProposal.executive_summary}

THE CHALLENGE
-------------
${generatedProposal.problem_statement}

OUR SOLUTION
------------
${generatedProposal.proposed_solution}

DELIVERABLES
------------
${generatedProposal.deliverables.map(d => `• ${d}`).join('\n')}

PRICING
-------
Package: ${generatedProposal.pricing.package}
Monthly: RM ${generatedProposal.pricing.final_monthly}/month
${generatedProposal.pricing.discount_percent > 0 ? `Discount: ${generatedProposal.pricing.discount_percent}% off` : ''}
Annual Total: RM ${generatedProposal.pricing.annual_total.toLocaleString()}

TIMELINE
--------
${generatedProposal.timeline}

TERMS & CONDITIONS
------------------
${generatedProposal.terms.map(t => `• ${t}`).join('\n')}

NEXT STEPS
----------
${generatedProposal.next_steps.map(n => `• ${n}`).join('\n')}

---
Generated by MySales Orbit AI
    `.trim();

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${generatedProposal.proposal_id}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-violet-600 to-cyan-500 rounded-xl">
              <Brain className="w-6 h-6 text-white" />
            </div>
            AI Command Center
          </h1>
          <p className="text-slate-400 mt-1">Powered by GPT-4 • Intelligent sales automation</p>
        </div>
        <Button onClick={fetchInsights} variant="outline" className="border-slate-700">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh Insights
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-slate-800 pb-2 overflow-x-auto">
        {[
          { id: 'insights', label: 'Deal Insights', icon: TrendingUp },
          { id: 'email', label: 'AI Email Writer', icon: Mail },
          { id: 'meeting', label: 'Meeting Summarizer', icon: FileText },
          { id: 'proposal', label: 'Proposal Generator', icon: FileSignature }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === tab.id 
                ? 'bg-gradient-to-r from-violet-600 to-cyan-500 text-white' 
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'insights' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          {insights && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-slate-900/50 border-slate-800 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-400 text-sm">Total Leads</p>
                      <p className="text-2xl font-bold text-white">{insights.total_leads}</p>
                    </div>
                    <Users className="w-8 h-8 text-blue-400" />
                  </div>
                </Card>
                <Card className="bg-slate-900/50 border-slate-800 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-400 text-sm">Pipeline Value</p>
                      <p className="text-2xl font-bold text-white">RM {insights.total_value?.toLocaleString()}</p>
                    </div>
                    <DollarSign className="w-8 h-8 text-green-400" />
                  </div>
                </Card>
                <Card className="bg-slate-900/50 border-slate-800 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-400 text-sm">Win Rate</p>
                      <p className="text-2xl font-bold text-white">{insights.win_rate}%</p>
                    </div>
                    <Target className="w-8 h-8 text-violet-400" />
                  </div>
                </Card>
                <Card className="bg-slate-900/50 border-slate-800 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-400 text-sm">Pipeline Health</p>
                      <p className={`text-lg font-bold ${getHealthColor(insights.pipeline_health).split(' ')[0]}`}>
                        {insights.pipeline_health}
                      </p>
                    </div>
                    <div className={`p-2 rounded-lg ${getHealthColor(insights.pipeline_health)}`}>
                      <Zap className="w-6 h-6" />
                    </div>
                  </div>
                </Card>
              </div>

              {/* AI Insights */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="bg-slate-900/50 border-slate-800 p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-violet-400" />
                    AI Insights
                  </h3>
                  <div className="space-y-3">
                    {insights.insights?.map((insight, i) => (
                      <div key={i} className={`p-3 rounded-lg border ${
                        insight.priority === 'high' ? 'bg-red-500/10 border-red-500/30' :
                        insight.priority === 'medium' ? 'bg-yellow-500/10 border-yellow-500/30' :
                        'bg-slate-800/50 border-slate-700'
                      }`}>
                        <div className="flex items-start gap-2">
                          {insight.type === 'warning' ? (
                            <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                          ) : (
                            <Sparkles className="w-4 h-4 text-violet-400 mt-0.5 flex-shrink-0" />
                          )}
                          <p className="text-slate-300 text-sm">{insight.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                <Card className="bg-slate-900/50 border-slate-800 p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-cyan-400" />
                    Recommendations
                  </h3>
                  <div className="space-y-3">
                    {insights.recommendations?.map((rec, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg">
                        <div className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {i + 1}
                        </div>
                        <p className="text-slate-300 text-sm">{rec}</p>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>

              {/* At-Risk Deals */}
              {insights.at_risk_deals?.length > 0 && (
                <Card className="bg-slate-900/50 border-slate-800 p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    At-Risk Deals ({insights.at_risk_deals.length})
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-slate-400 text-sm border-b border-slate-700">
                          <th className="pb-3">Contact</th>
                          <th className="pb-3">Company</th>
                          <th className="pb-3">Value</th>
                          <th className="pb-3">Stage</th>
                          <th className="pb-3">Risk Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {insights.at_risk_deals.map((deal, i) => (
                          <tr key={i} className="border-b border-slate-800 text-sm">
                            <td className="py-3 text-white font-medium">{deal.contact_name}</td>
                            <td className="py-3 text-slate-300">{deal.company}</td>
                            <td className="py-3 text-green-400">RM {deal.value?.toLocaleString()}</td>
                            <td className="py-3">
                              <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs capitalize">
                                {deal.stage}
                              </span>
                            </td>
                            <td className="py-3 text-red-400 text-xs">{deal.risk_reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
            </>
          )}

          {loading && !insights && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-violet-400 animate-spin" />
              <span className="ml-3 text-slate-400">Analyzing your sales data...</span>
            </div>
          )}
        </div>
      )}

      {activeTab === 'email' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Email Generator Form */}
          <Card className="bg-slate-900/50 border-slate-800 p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-cyan-400" />
              Generate AI Email
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Select Lead</label>
                <select
                  value={selectedLead || ''}
                  onChange={(e) => setSelectedLead(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white"
                >
                  <option value="">Select a lead...</option>
                  {leads.map(lead => (
                    <option key={lead.id} value={lead.id}>
                      {lead.contact_name || lead.id} - {lead.stage} (RM {lead.value?.toLocaleString()})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Email Type</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'follow_up', label: 'Follow-up' },
                    { value: 'proposal', label: 'Proposal' },
                    { value: 'introduction', label: 'Introduction' },
                    { value: 'thank_you', label: 'Thank You' }
                  ].map(type => (
                    <button
                      key={type.value}
                      onClick={() => setEmailType(type.value)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        emailType === type.value
                          ? 'bg-gradient-to-r from-violet-600 to-cyan-500 text-white'
                          : 'bg-slate-800 text-slate-400 hover:text-white'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Additional Context (Optional)</label>
                <textarea
                  value={customContext}
                  onChange={(e) => setCustomContext(e.target.value)}
                  placeholder="E.g., Mention the discount we discussed, focus on their cloud migration needs..."
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder:text-slate-500 h-24 resize-none"
                />
              </div>

              <Button 
                onClick={generateEmail} 
                disabled={!selectedLead || loading}
                className="w-full bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-700 hover:to-cyan-600"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Generate Email
              </Button>
            </div>
          </Card>

          {/* Generated Email Preview */}
          <Card className="bg-slate-900/50 border-slate-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-violet-400" />
                Generated Email
              </h3>
              {generatedEmail && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(`Subject: ${generatedEmail.subject}\n\n${generatedEmail.body}`)}
                  className="border-slate-700"
                >
                  {copied ? <CheckCircle className="w-4 h-4 mr-1 text-green-400" /> : <Copy className="w-4 h-4 mr-1" />}
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              )}
            </div>

            {generatedEmail ? (
              <div className="space-y-4">
                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">To: {generatedEmail.lead_name} ({generatedEmail.company})</p>
                  <p className="text-sm text-slate-400">Subject:</p>
                  <p className="text-white font-medium">{generatedEmail.subject}</p>
                </div>
                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <p className="text-sm text-slate-400 mb-2">Body:</p>
                  <div className="text-slate-300 whitespace-pre-wrap text-sm leading-relaxed">
                    {generatedEmail.body}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-slate-500">
                <Mail className="w-12 h-12 mb-3 opacity-50" />
                <p>Select a lead and generate an email</p>
                <p className="text-sm mt-1">AI will personalize the content based on lead data</p>
              </div>
            )}
          </Card>
        </div>
      )}

      {activeTab === 'meeting' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Meeting Notes Input */}
          <Card className="bg-slate-900/50 border-slate-800 p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-cyan-400" />
              Meeting Notes
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Related Lead (Optional)</label>
                <select
                  value={selectedLead || ''}
                  onChange={(e) => setSelectedLead(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white"
                >
                  <option value="">No lead selected</option>
                  {leads.map(lead => (
                    <option key={lead.id} value={lead.id}>
                      {lead.contact_name || lead.id} - {lead.stage}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Paste Meeting Notes</label>
                <textarea
                  value={meetingNotes}
                  onChange={(e) => setMeetingNotes(e.target.value)}
                  placeholder="Paste your meeting notes, call transcript, or discussion points here...

Example:
- Discussed pricing for 10-user license
- Client interested in API integration
- Follow up next week with proposal
- Need to schedule demo for technical team"
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder:text-slate-500 h-64 resize-none"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="createTasks"
                  checked={createTasks}
                  onChange={(e) => setCreateTasks(e.target.checked)}
                  className="rounded border-slate-700 bg-slate-800 text-violet-500"
                />
                <label htmlFor="createTasks" className="text-sm text-slate-400">
                  Auto-create tasks from action items
                </label>
              </div>

              <Button 
                onClick={summarizeMeeting} 
                disabled={!meetingNotes.trim() || loading}
                className="w-full bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-700 hover:to-cyan-600"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Brain className="w-4 h-4 mr-2" />}
                Analyze & Summarize
              </Button>
            </div>
          </Card>

          {/* Summary Output */}
          <Card className="bg-slate-900/50 border-slate-800 p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-400" />
              AI Summary
            </h3>

            {meetingSummary ? (
              <div className="space-y-4">
                {/* Sentiment Badge */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-400">Sentiment:</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSentimentColor(meetingSummary.sentiment)} bg-slate-800`}>
                    {meetingSummary.sentiment}
                  </span>
                  {meetingSummary.tasks_created > 0 && (
                    <span className="px-3 py-1 rounded-full text-sm font-medium text-green-400 bg-green-400/10">
                      {meetingSummary.tasks_created} tasks created
                    </span>
                  )}
                </div>

                {/* Summary */}
                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <h4 className="text-sm font-medium text-violet-400 mb-2">Summary</h4>
                  <p className="text-slate-300 text-sm">{meetingSummary.summary}</p>
                </div>

                {/* Key Points */}
                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <h4 className="text-sm font-medium text-cyan-400 mb-2">Key Points</h4>
                  <ul className="space-y-2">
                    {meetingSummary.key_points?.map((point, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                        <ChevronRight className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Action Items */}
                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <h4 className="text-sm font-medium text-green-400 mb-2">Action Items</h4>
                  <ul className="space-y-2">
                    {meetingSummary.action_items?.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                        <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Next Steps */}
                <div className="p-4 bg-gradient-to-r from-violet-600/20 to-cyan-500/20 rounded-lg border border-violet-500/30">
                  <h4 className="text-sm font-medium text-white mb-2">Next Steps</h4>
                  <p className="text-slate-300 text-sm">{meetingSummary.next_steps}</p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-slate-500">
                <Brain className="w-12 h-12 mb-3 opacity-50" />
                <p>Paste meeting notes and click analyze</p>
                <p className="text-sm mt-1">AI will extract key points and action items</p>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Proposal Generator Tab */}
      {activeTab === 'proposal' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Proposal Generator Form */}
          <Card className="bg-slate-900/50 border-slate-800 p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-orange-400" />
              Generate AI Proposal
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Select Lead</label>
                <select
                  value={selectedLead || ''}
                  onChange={(e) => setSelectedLead(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white"
                >
                  <option value="">Select a lead...</option>
                  {leads.map(lead => (
                    <option key={lead.id} value={lead.id}>
                      {lead.contact_name || lead.id} - {lead.stage} (RM {lead.value?.toLocaleString()})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Package Tier</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'starter', label: 'Starter', price: 'RM 199/mo' },
                    { value: 'professional', label: 'Professional', price: 'RM 399/mo' },
                    { value: 'enterprise', label: 'Enterprise', price: 'RM 799/mo' }
                  ].map(pkg => (
                    <button
                      key={pkg.value}
                      onClick={() => setPackageTier(pkg.value)}
                      className={`px-3 py-3 rounded-lg text-sm font-medium transition-all ${
                        packageTier === pkg.value
                          ? 'bg-gradient-to-r from-orange-600 to-amber-500 text-white'
                          : 'bg-slate-800 text-slate-400 hover:text-white'
                      }`}
                    >
                      <div>{pkg.label}</div>
                      <div className="text-xs opacity-80">{pkg.price}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="includeDiscount"
                    checked={includeDiscount}
                    onChange={(e) => setIncludeDiscount(e.target.checked)}
                    className="rounded border-slate-700 bg-slate-800 text-orange-500"
                  />
                  <label htmlFor="includeDiscount" className="text-sm text-slate-400">
                    Include Discount
                  </label>
                </div>
                {includeDiscount && (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={discountPercent}
                      onChange={(e) => setDiscountPercent(parseInt(e.target.value) || 0)}
                      min="1"
                      max="50"
                      className="w-16 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-white text-sm"
                    />
                    <Percent className="w-4 h-4 text-slate-400" />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Custom Notes (Optional)</label>
                <textarea
                  value={proposalNotes}
                  onChange={(e) => setProposalNotes(e.target.value)}
                  placeholder="E.g., Emphasize API integration capabilities, mention competitor comparison..."
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder:text-slate-500 h-20 resize-none"
                />
              </div>

              <Button 
                onClick={generateProposal} 
                disabled={!selectedLead || loading}
                className="w-full bg-gradient-to-r from-orange-600 to-amber-500 hover:from-orange-700 hover:to-amber-600"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Generate Proposal
              </Button>
            </div>
          </Card>

          {/* Generated Proposal Preview */}
          <Card className="bg-slate-900/50 border-slate-800 p-6 max-h-[700px] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-orange-400" />
                Proposal Preview
              </h3>
              {generatedProposal && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(JSON.stringify(generatedProposal, null, 2))}
                    className="border-slate-700"
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </Button>
                  <Button
                    size="sm"
                    onClick={downloadProposal}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Download
                  </Button>
                </div>
              )}
            </div>

            {generatedProposal ? (
              <div className="space-y-4">
                {/* Header */}
                <div className="p-4 bg-gradient-to-r from-orange-600/20 to-amber-500/20 rounded-lg border border-orange-500/30">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="text-xs text-orange-400">Proposal ID</p>
                      <p className="text-white font-mono">{generatedProposal.proposal_id}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-400">Valid Until</p>
                      <p className="text-white">{generatedProposal.valid_until}</p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-orange-500/30">
                    <p className="text-xs text-slate-400">Prepared For</p>
                    <p className="text-white font-semibold">{generatedProposal.client_name}</p>
                    <p className="text-slate-300 text-sm">{generatedProposal.company}</p>
                  </div>
                </div>

                {/* Executive Summary */}
                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <h4 className="text-sm font-medium text-orange-400 mb-2">Executive Summary</h4>
                  <p className="text-slate-300 text-sm">{generatedProposal.executive_summary}</p>
                </div>

                {/* Problem & Solution */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <h4 className="text-sm font-medium text-red-400 mb-2">The Challenge</h4>
                    <p className="text-slate-300 text-sm">{generatedProposal.problem_statement}</p>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <h4 className="text-sm font-medium text-green-400 mb-2">Our Solution</h4>
                    <p className="text-slate-300 text-sm">{generatedProposal.proposed_solution}</p>
                  </div>
                </div>

                {/* Deliverables */}
                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <h4 className="text-sm font-medium text-cyan-400 mb-2">Deliverables</h4>
                  <ul className="space-y-2">
                    {generatedProposal.deliverables.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                        <CheckCircle className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Pricing */}
                <div className="p-4 bg-gradient-to-r from-green-600/20 to-emerald-500/20 rounded-lg border border-green-500/30">
                  <h4 className="text-sm font-medium text-green-400 mb-3">Pricing</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-slate-400">Package</p>
                      <p className="text-white font-semibold">{generatedProposal.pricing.package}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Users</p>
                      <p className="text-white">{generatedProposal.pricing.users}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Monthly</p>
                      <p className="text-white">RM {generatedProposal.pricing.final_monthly}/mo</p>
                      {generatedProposal.pricing.discount_percent > 0 && (
                        <p className="text-green-400 text-xs">{generatedProposal.pricing.discount_percent}% off!</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Annual Total</p>
                      <p className="text-2xl font-bold text-green-400">RM {generatedProposal.pricing.annual_total.toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <h4 className="text-sm font-medium text-violet-400 mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Timeline
                  </h4>
                  <p className="text-slate-300 text-sm">{generatedProposal.timeline}</p>
                </div>

                {/* Terms & Next Steps */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <h4 className="text-sm font-medium text-slate-400 mb-2">Terms</h4>
                    <ul className="space-y-1">
                      {generatedProposal.terms.map((term, i) => (
                        <li key={i} className="text-xs text-slate-400">• {term}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <h4 className="text-sm font-medium text-orange-400 mb-2">Next Steps</h4>
                    <ul className="space-y-1">
                      {generatedProposal.next_steps.map((step, i) => (
                        <li key={i} className="text-xs text-slate-300">
                          <span className="text-orange-400 font-bold">{i + 1}.</span> {step}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-slate-500">
                <FileSignature className="w-12 h-12 mb-3 opacity-50" />
                <p>Select a lead and generate a proposal</p>
                <p className="text-sm mt-1">AI will create a professional sales proposal</p>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
